import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Button, Card, Space, Spin, Tooltip, Empty } from 'antd'
import { VariableSizeList as List } from 'react-window'
import useSWR from 'swr'
import { useToken } from '../../hooks'
import API from '../../../utils/api'
import { DownloadOutlined, ReloadOutlined, OrderedListOutlined } from '@ant-design/icons'
import styled, { keyframes } from 'styled-components'
import { goTo } from '../../hooks/navigation'
import { useHistory } from 'react-router'
import * as XLSX from 'xlsx'
import DateSearch from './DateSearch'
import dayjs from 'dayjs'

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

const SkeletonCell = styled.div<{ width?: number }>`
  height: 16px;
  width: ${props => props.width ? `${props.width}px` : '80%'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
  border-radius: 2px;
  display: inline-block;
`

const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 4px;
  overflow: hidden;
`

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.95);
  height: 40px;
  position: sticky;
  top: 0;
  z-index: 10;
`

const TableRow = styled.div<{ $selected?: boolean; $even?: boolean }>`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  color: #000;
  font-weight: 400;
  background: ${props => props.$selected ? 'rgba(92, 124, 250, 0.05)' : (props.$even ? '#fafafa' : 'white')};
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(92, 124, 250, 0.08);
  }
`

const TableCell = styled.div<{ width?: number; align?: string }>`
  flex: ${props => props.width ? `0 0 ${props.width}px` : '1'};
  padding: 8px 12px;
  text-align: ${props => props.align || 'left'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-right: 1px solid #f5f5f5;
  
  &:last-child {
    border-right: none;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  flex: 1;
`

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 300px;
  background: #fafafa;
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
`

const PaginationButton = styled.button<{ $active?: boolean }>`
  padding: 4px 12px;
  margin: 0 2px;
  font-size: 12px;
  border: 1px solid ${props => props.$active ? '#5c7cfa' : '#d9d9d9'};
  background: ${props => props.$active ? '#5c7cfa' : 'white'};
  color: ${props => props.$active ? 'white' : 'rgba(0, 0, 0, 0.85)'};
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #5c7cfa;
    color: #5c7cfa;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

interface OptimizedTableAPIProps {
  title?: string
  apikey: string
  columns: any
  height?: number | string
  detail?: string | boolean
  datesearch?: boolean
  defaultMaxData?: number
  autorefresh?: number
  onRowClick?: (record: any) => void
  extra?: React.ReactNode
  stylewidth?: number
  width?: number
  walletlist?: boolean
  footer?: number | boolean
  fold?: any
  uid?: any
  setSC?: (data: any) => void
}

const OptimizedTableAPI: React.FC<OptimizedTableAPIProps> = ({
  title,
  apikey,
  columns,
  height = 400,
  detail = '',
  datesearch = false,
  defaultMaxData = 100,
  autorefresh = 0,
  onRowClick,
  extra,
  stylewidth,
  width,
  walletlist = false,
  footer = false,
  fold,
  uid,
  setSC
}) => {
  const { data: token } = useToken()
  const nav = useHistory()
  
  const [page, setPage] = useState(1)
  const [maxData, setMaxData] = useState(defaultMaxData || 100)
  const [startDate, setStartDate] = useState<any>(null)
  const [endDate, setEndDate] = useState<any>(null)
  const [type, setType] = useState(0)
  
  // Initialize date range
  useEffect(() => {
    if (!datesearch) return
    
    const now = dayjs()
    setType(0)
    if (now.hour() < 6) {
      setStartDate(now.subtract(8, 'day').format('YYYY-MM-DD 06:00:00'))
      setEndDate(now.format('YYYY-MM-DD 05:59:59'))
    } else {
      setStartDate(now.subtract(7, 'day').format('YYYY-MM-DD 06:00:00'))
      setEndDate(now.add(1, 'day').format('YYYY-MM-DD 05:59:59'))
    }
  }, [datesearch])
  
  // Build API URL
  const dateurl = useMemo(() => 
    `${(startDate && `&startdate=${startDate}`) || ''}${(endDate && `&enddate=${endDate}`) || ''}`,
    [startDate, endDate]
  )
  
  const KEY = useMemo(() => 
    apikey ? `${apikey}${dateurl}&maxData=${maxData}&page=${page}` : null,
    [apikey, dateurl, maxData, page]
  )
  
  // Fetch data
  const { data, mutate, isLoading } = useSWR<any>(
    KEY,
    async () => {
      const response = await API.get(`${KEY}`, token + '', {})
      return response?.result?.result
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 0,
      refreshInterval: autorefresh || undefined
    }
  )
  
  // Process data source with memory optimization
  const dataSource = useMemo(() => {
    // Show skeleton only on very first load to reduce memory
    if (isLoading && !data) {
      return Array(5).fill(null).map((_, i) => ({ _skeleton: true, _id: `skeleton_${i}` })) // Reduced from 10 to 5
    }
    
    // Return actual data or empty array
    return data?.list || []
  }, [data, isLoading])
  
  // Get processed columns
  const processedColumns = useMemo(() => {
    if (typeof columns === 'function') {
      return columns(() => {}, () => {}, token, fold, null, null, null, uid, setSC)
    }
    return columns
  }, [columns, token, fold, uid, setSC])
  
  // Row renderer for virtual list
  const Row = useCallback(({ index, style }) => {
    const record = dataSource[index]
    
    // Show skeleton only for initial loading
    if (record?._skeleton) {
      return (
        <TableRow 
          style={style} 
          $even={index % 2 === 0}
        >
          {processedColumns.map((col: any, colIndex: number) => (
            <TableCell 
              key={colIndex}
              width={col.width}
              align={col.align}
            >
              <SkeletonCell width={col.width ? Math.floor(col.width * 0.7) : 80} />
            </TableCell>
          ))}
        </TableRow>
      )
    }
    
    // Don't render anything if no record (for scrolling)
    if (!record) return null
    
    return (
      <TableRow 
        style={style} 
        $even={index % 2 === 0}
        onClick={() => onRowClick?.(record)}
      >
        {processedColumns.map((col: any, colIndex: number) => {
          const cellContent = col.render ? col.render(record[col.dataIndex], record) : record[col.dataIndex]
          
          return (
            <TableCell 
              key={colIndex}
              width={col.width}
              align={col.align}
            >
              {cellContent}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }, [dataSource, processedColumns, onRowClick])
  
  // Export to Excel
  const exportToExcel = useCallback(() => {
    if (!dataSource?.length) return
    
    try {
      const exportData = dataSource.map((item: any) => {
        const row: any = {}
        processedColumns.forEach((col: any) => {
          if (col.dataIndex) {
            row[col.title || col.dataIndex] = item[col.dataIndex]
          }
        })
        return row
      })
      
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)
      XLSX.utils.book_append_sheet(wb, ws, 'Data')
      
      const fileName = `${title || 'Export'}_${new Date().getTime()}.xlsx`
      XLSX.writeFile(wb, fileName)
    } catch (error) {
      console.error('Excel export error:', error)
    }
  }, [dataSource, processedColumns, title])
  
  // Calculate total pages
  const totalPages = Math.ceil((data?.totalCount || 0) / maxData)
  
  // Generate page numbers to show
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = []
    const maxButtons = 7
    
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      } else if (page >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = page - 1; i <= page + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      }
    }
    
    return pages
  }, [page, totalPages])
  
  return (
    <Card
      title={title}
      size="small"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      extra={
        <Space size="small">
          {extra}
          
          {datesearch && (
            <DateSearch
              type={type}
              setType={setType}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          )}
          
          {detail && (
            <Button
              onClick={() => goTo(nav, detail)}
              type="ghost"
              size="small"
            >
              <OrderedListOutlined />
            </Button>
          )}
          
          <Button
            icon={<DownloadOutlined />}
            type="ghost"
            size="small"
            onClick={exportToExcel}
          />
          
          <Button
            onClick={() => mutate()}
            type="ghost"
            size="small"
          >
            <ReloadOutlined />
          </Button>
        </Space>
      }
      bodyStyle={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      <TableContainer>
        {/* Header */}
        <TableHeader>
          {processedColumns.map((col: any, index: number) => {
            // 축약된 제목에 대한 전체 텍스트 매핑
            const fullTitles: { [key: string]: string } = {
              '프...': '프론트시간',
              '프론트시...': '프론트시간',
              '프론트...': '프론트시간',
              '내역': '거래내역',
              '비고': '비고사항',
              '상세': '상세정보'
            }
            
            const displayTitle = col.title || ''
            const tooltipTitle = fullTitles[displayTitle] || displayTitle
            const needsTooltip = fullTitles[displayTitle] || (displayTitle && displayTitle.includes('...'))
            
            return (
              <TableCell key={index} width={col.width} align={col.align}>
                {needsTooltip ? (
                  <Tooltip title={tooltipTitle}>
                    <span>{displayTitle}</span>
                  </Tooltip>
                ) : (
                  displayTitle
                )}
              </TableCell>
            )
          })}
        </TableHeader>
        
        {/* Body with virtual scrolling */}
        {isLoading && !data ? (
          <List
            height={typeof height === 'number' ? height : 400}
            itemCount={5} // Reduced skeleton items
            itemSize={() => 40}
            width="100%"
            overscanCount={2} // Reduced overscan for skeleton
          >
            {Row}
          </List>
        ) : dataSource.length === 0 ? (
          <EmptyContainer>
            <Empty 
              description={
                <span style={{ color: '#666', fontSize: 13 }}>
                  조회된 데이터가 없습니다
                </span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </EmptyContainer>
        ) : (
          <List
            height={typeof height === 'number' ? height : 400}
            itemCount={dataSource.length}
            itemSize={() => 40}
            width="100%"
            overscanCount={5} // Reduced from 15 to 5 for better memory
          >
            {Row}
          </List>
        )}
        
        {/* Pagination */}
        <PaginationContainer>
          <div>
            총 {data?.totalCount || 0}개 | 
            페이지당 
            <select 
              value={maxData} 
              onChange={(e) => {
                setMaxData(Number(e.target.value))
                setPage(1)
              }}
              style={{ margin: '0 4px' }}
            >
              <option value={100}>100</option>
              <option value={50}>50</option>
              <option value={20}>20</option>
              <option value={200}>200</option>
            </select>
            개
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PaginationButton
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              처음
            </PaginationButton>
            <PaginationButton
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              이전
            </PaginationButton>
            
            {pageNumbers.map((num, index) => 
              num === '...' ? (
                <span key={index} style={{ margin: '0 8px' }}>...</span>
              ) : (
                <PaginationButton
                  key={index}
                  $active={page === num}
                  onClick={() => setPage(Number(num))}
                >
                  {num}
                </PaginationButton>
              )
            )}
            
            <PaginationButton
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              다음
            </PaginationButton>
            <PaginationButton
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              마지막
            </PaginationButton>
            
            <span style={{ margin: '0 12px' }}>|</span>
            
            <input
              type="number"
              min={1}
              max={totalPages}
              value={page}
              onChange={(e) => {
                const newPage = Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1))
                setPage(newPage)
              }}
              style={{
                width: '60px',
                height: '28px',
                textAlign: 'center',
                border: '1px solid #d9d9d9',
                borderRadius: '2px',
                fontSize: '12px',
                marginRight: '4px'
              }}
            />
            <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }}>
              / {totalPages}
            </span>
          </div>
        </PaginationContainer>
      </TableContainer>
    </Card>
  )
}

export default React.memo(OptimizedTableAPI)